import DOMPurify from 'dompurify';

/**
 * Shared HTML sanitizer for every place the author app injects raw HTML via
 * `dangerouslySetInnerHTML`.
 *
 * These sinks render author-controlled content (book description /
 * feedbackGuidelines / copyRightInfo, broadcast message bodies, manuscript
 * upload previews). Historically none of it was sanitized, which allowed
 * stored XSS: an author could put `<script>` / `<img onerror>` / a
 * `javascript:` link into one of these fields and have it execute in a
 * reader's (or another author's) browser.
 *
 * The allowlist keeps the legitimate rich-text formatting these fields carry —
 * plain text with `<br>`, plus the tags produced by draftjs-to-html and by the
 * docx→HTML manuscript conversion (paragraphs, bold/italic/underline, lists,
 * headings, blockquotes, links, images, simple tables) — and strips
 * everything dangerous: `<script>`, event handlers (`on*`), `javascript:` /
 * `data:` URIs, `<iframe>`, `<style>`, `<object>`/`<embed>`, form controls,
 * etc. `javascript:` and `data:` URIs are already rejected by DOMPurify's
 * default `ALLOWED_URI_REGEXP`, so no custom URI handling is required.
 */

// Rich-text formatting tags. Deliberately excludes script/iframe/style/object/
// embed/form/input and any interactive or resource-loading element beyond
// links and images.
const ALLOWED_TAGS = [
  // block / structure
  'p', 'br', 'hr', 'div', 'span',
  // inline formatting
  'strong', 'b', 'em', 'i', 'u', 'ins', 'del', 's', 'strike',
  'sub', 'sup', 'mark', 'small', 'abbr', 'cite', 'q',
  // headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // lists
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  // quotes / code
  'blockquote', 'pre', 'code',
  // links / media
  'a', 'img',
  // simple tables (docx conversion can emit these)
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption',
  'colgroup', 'col',
  'figure', 'figcaption',
];

// Attributes kept on the allowed tags. `style` is retained so alignment/colour
// from draftjs-to-html survives; DOMPurify sanitizes the CSS value itself
// (dropping `url(javascript:…)`, `expression()`, etc.). `on*` handlers are
// never listed, so they are always stripped.
const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title', 'name',
  'src', 'alt', 'width', 'height',
  'class', 'style', 'dir', 'lang',
  'colspan', 'rowspan', 'align', 'valign', 'scope',
];

// NB on images: DOMPurify's defaults already permit `data:` URIs on <img>
// (its built-in DATA_URI_TAGS) while rejecting `data:`/`javascript:` on <a href>.
// That is exactly what we want: the docx→HTML manuscript conversion (mammoth,
// with no custom convertImage) inlines images as `<img src="data:image/png;
// base64,…">`, and those must survive the chapter-upload preview. An SVG data
// URI in an <img src> renders in the browser's restricted image mode (no script
// execution, no external fetches), so it is not an XSS vector. Hence no custom
// ALLOWED_URI_REGEXP is needed — the defaults are both safe and non-destructive.

// DOMPurify does not scrub the *value* of an allowed `style` attribute, so a
// payload like `style="background:url(javascript:alert(1))"` survives. Those
// vectors are inert in modern browsers, but we drop them anyway to stay
// conservative. Legitimate draftjs-to-html styling (text-align, color,
// font-size/-family, hex background-color) never uses any of these tokens, so
// scrubbing them preserves all real formatting.
const DANGEROUS_CSS = /url\(|expression|javascript:|@import|behavior|-moz-binding|<|\\/i;

// Registered once for the module lifetime.
let hookRegistered = false;
function registerHook(): void {
  if (hookRegistered) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Force reader-safety on any link that opens a new tab (prevents reverse
    // tabnabbing via window.opener).
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
    // Scrub dangerous CSS from surviving style attributes.
    const style = node.getAttribute && node.getAttribute('style');
    if (style && DANGEROUS_CSS.test(style)) {
      node.removeAttribute('style');
    }
  });
  hookRegistered = true;
}

/**
 * Sanitize a raw HTML string against the shared rich-text allowlist.
 *
 * @param dirty Untrusted HTML (may be null/undefined — returns '').
 * @returns Safe HTML string with only allowlisted tags/attributes.
 */
export function sanitizeHtml(dirty?: string | null): string {
  if (dirty == null) return '';
  registerHook();
  return DOMPurify.sanitize(String(dirty), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // Belt-and-braces: even if one of these slipped into ALLOWED_TAGS above,
    // it would still be dropped.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form',
      'input', 'button', 'textarea', 'select', 'link', 'meta', 'base'],
    FORBID_ATTR: ['srcset', 'formaction', 'xlink:href'],
  });
}

export default sanitizeHtml;
